import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, FileText, Beaker } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductCatalog() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then(res => setProducts(res.data)).catch(() => {});
  }, []);

  const scrollToRFQ = () => {
    const el = document.querySelector('#rfq');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  if (products.length === 0) return null;

  // Show only first 4 products on homepage, rest on /products page
  const featured = products.slice(0, 4);

  return (
    <section id="products" data-testid="products-section" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <div className="flex items-end justify-between mb-16">
          <div>
            <span
              className="text-xs tracking-widest uppercase text-[#0A7C84] mb-3 block"
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              Product Catalog
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1128] mb-4"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
              data-testid="products-heading"
            >
              Peptide APIs
            </h2>
            <p
              className="text-base md:text-lg text-[#495057] max-w-2xl"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Analytically verified, batch-consistent compounds for pharmaceutical development, research, and cosmetic formulation.
            </p>
          </div>
          <Link
            to="/products"
            className="hidden md:flex items-center gap-2 px-5 py-2 rounded-full border border-[#DEE2E6] text-sm text-[#495057] hover:bg-[#F8F9FA] transition-colors duration-150 shrink-0"
            style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
            data-testid="view-all-products"
          >
            View All {products.length} Products
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#DEE2E6]">
          {featured.map((product, idx) => (
            <div
              key={product.id}
              className={`p-8 md:p-12 ${idx % 2 === 0 ? 'md:border-r border-[#DEE2E6]' : ''} ${idx < 2 ? 'border-b border-[#DEE2E6]' : ''}`}
              data-testid={`product-card-${product.slug}`}
            >
              {/* Product Image */}
              <Link to={`/products/${product.slug}`} className="block relative mb-8 bg-[#F8F9FA] border border-[#DEE2E6] overflow-hidden aspect-[4/3] flex items-center justify-center group">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  data-testid={`product-image-${product.slug}`}
                />
              </Link>

              {/* Product Info */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <Link
                    to={`/products/${product.slug}`}
                    className="text-2xl tracking-tight text-[#0A1128] mb-1 hover:text-[#0A7C84] transition-colors duration-150 block"
                    style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}
                    data-testid={`product-name-${product.slug}`}
                  >
                    {product.name}
                  </Link>
                  <p className="text-sm text-[#6C757D]" style={{ fontFamily: 'var(--font-body)' }}>
                    {product.tagline}
                  </p>
                </div>
                <Badge variant="outline" className="text-[#0A7C84] border-[#0A7C84]/20 rounded-none text-xs"
                  style={{ fontFamily: 'var(--font-mono)' }}>
                  {product.status === 'active' ? 'In Stock' : 'Inquiry'}
                </Badge>
              </div>

              <p className="text-sm text-[#495057] mb-6 leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                {product.description}
              </p>

              {/* Technical Specs */}
              <div className="border-t border-[#DEE2E6] pt-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Beaker className="w-4 h-4 text-[#0A7C84]" strokeWidth={1.5} />
                  <span className="text-xs tracking-widest uppercase text-[#6C757D]"
                    style={{ fontFamily: 'var(--font-mono)' }}>Technical Specifications</span>
                </div>
                <div className="grid grid-cols-2 gap-y-3 gap-x-6">
                  {[
                    { label: 'CAS Number', value: product.cas_number },
                    { label: 'Mol. Weight', value: product.molecular_weight },
                    { label: 'Formula', value: product.molecular_formula },
                    { label: 'Purity', value: product.purity },
                    { label: 'Form', value: product.form },
                    { label: 'Storage', value: product.storage },
                  ].map(spec => (
                    <div key={spec.label}>
                      <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-0.5"
                        style={{ fontFamily: 'var(--font-mono)' }}>{spec.label}</span>
                      <span className="text-sm text-[#0A1128]"
                        style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}
                        data-testid={`product-${product.slug}-${spec.label.toLowerCase().replace(/[. ]/g, '-')}`}>
                        {spec.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Applications */}
              {product.applications && product.applications.length > 0 && (
                <div className="mb-6">
                  <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-2"
                    style={{ fontFamily: 'var(--font-mono)' }}>Applications</span>
                  <div className="flex flex-wrap gap-2">
                    {product.applications.map((app, i) => (
                      <span key={i} className="px-2 py-1 text-xs bg-[#F8F9FA] border border-[#DEE2E6] text-[#495057]"
                        style={{ fontFamily: 'var(--font-body)' }}>
                        {app}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-[#DEE2E6]">
                <Link
                  to={`/products/${product.slug}`}
                  className="group btn-pill flex items-center gap-2 px-5 py-2 bg-[#0A7C84] text-white text-sm hover:bg-[#0A1128] transition-colors duration-150"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                  data-testid={`product-view-${product.slug}`}
                >
                  View Details
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={1.5} />
                </Link>
                <button
                  onClick={scrollToRFQ}
                  className="btn-pill flex items-center gap-2 px-5 py-2 border border-[#DEE2E6] text-[#495057] text-sm hover:bg-[#F8F9FA] transition-colors duration-150"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                  data-testid={`product-rfq-${product.slug}`}
                >
                  Request Quote
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile View All */}
        <Link
          to="/products"
          className="md:hidden flex items-center justify-center gap-2 w-full mt-6 px-5 py-3 rounded-full border border-[#DEE2E6] text-sm text-[#495057] hover:bg-[#F8F9FA] transition-colors duration-150"
          style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
          data-testid="view-all-products-mobile"
        >
          View All {products.length} Products
          <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </div>
    </section>
  );
}
